/**
 * AuthContext - manages login state, token storage, and auth API calls
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { API } from '@/config/constants';
import {
  apiFetch,
  AUTH_INVALID_EVENT,
  beginSessionTransition,
  clearStoredSession,
  getActiveAuthToken,
  getActiveSessionRevision,
  getActiveSessionType,
  hydrateStoredSession,
  isSessionTransitionCurrent,
  storeCloudSession,
  storeOfflineSession,
} from '@/api/authenticatedClient';
import { offlineAdminLogin } from '@/api/desktopSync';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  // A user is accepted together with the credential revision that established
  // it. Never retag an older React user with a live, newer session revision.
  const [sessionBinding, setSessionBinding] = useState(null);
  const user = sessionBinding?.user || null;
  const [loading, setLoading] = useState(true);
  const mounted = useRef(false);

  const ownsTransition = useCallback((owner) => (
    mounted.current && isSessionTransitionCurrent(owner)
  ), []);

  const requireTransition = useCallback((owner) => {
    if (!ownsTransition(owner)) {
      throw new Error('Sign-in was superseded by a newer authentication action.');
    }
  }, [ownsTransition]);

  const publishUser = useCallback((currentUser) => {
    setSessionBinding(Object.freeze({
      user: currentUser,
      revision: getActiveSessionRevision(),
      source: getActiveSessionType(),
    }));
  }, []);

  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    const owner = beginSessionTransition();
    const ownsRestore = () => !cancelled && ownsTransition(owner);
    const restoreSession = async () => {
      const nativeSession = await hydrateStoredSession(owner);
      if (!ownsRestore()) return;
      // Native memory survives a renderer reload, including a local-backend
      // restart. Do not require a reachable server just to recover that user.
      if (nativeSession?.user) publishUser(nativeSession.user);
      const token = getActiveAuthToken();
      if (!token) return;
      const sessionType = getActiveSessionType();
      try {
        const r = await apiFetch(`${API}/auth/me`);
        // Only a genuine auth failure invalidates the session. A network or
        // parse error must not silently log out a legitimate user.
        if (r.status === 401) {
          // The request client owns invalidation of its captured session. A
          // second clear here could instead erase a newer login.
          return;
        }
        if (r.ok) {
          try {
            const currentUser = await r.json();
            if (ownsRestore()) publishUser({
              ...currentUser,
              session_type: currentUser.session_type || sessionType,
            });
          } catch {
            /* malformed response — keep the session, leave user as-is */
          }
        }
      } catch {
        /* network error — keep the token; the user isn't unauthenticated */
      }
    };
    restoreSession().finally(() => {
      if (ownsRestore()) setLoading(false);
    });
    return () => {
      cancelled = true;
      mounted.current = false;
    };
  }, [ownsTransition, publishUser]);

  const login = useCallback(async (username, password) => {
    const owner = beginSessionTransition();
    try {
      if (window.lvbDesktop) {
        if (typeof window.lvbDesktop.cloudLogin !== 'function') {
          throw new Error('Desktop sign-in is unavailable. Restart the updated app and try again.');
        }
        await clearStoredSession(owner);
        requireTransition(owner);
        setSessionBinding(null);
        const data = await window.lvbDesktop.cloudLogin({ username, password });
        requireTransition(owner);
        if (!data?.ok) {
          throw new Error(data?.reason === 'invalid_credentials'
            ? 'Sign-in failed. Check your username, password and assigned role.'
            : 'Could not sign in online. Check your connection and try again.');
        }
        const stored = await storeCloudSession(data.token, data.user, owner);
        requireTransition(owner);
        if (!stored) throw new Error('Sign-in was superseded by a newer authentication action.');
        publishUser(data.user);
        if (data.user.roles.includes('admin') && !data.offlineReadiness?.cached) {
          toast.warning('Signed in online. Offline sign-in could not be prepared on this computer. Sign in online again to retry.', { duration: 10000 });
        }
        if (data.localReady === false) {
          toast.warning('Signed in online. Local services are reconnecting; offline work is not ready yet.', { duration: 10000 });
        }
        return data.user;
      }
      const res = await apiFetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      requireTransition(owner);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        requireTransition(owner);
        throw new Error(err.detail || 'Login failed');
      }
      const data = await res.json();
      requireTransition(owner);
      const stored = await storeCloudSession(data.token, data.user, owner);
      requireTransition(owner);
      if (!stored) throw new Error('Sign-in was superseded by a newer authentication action.');
      const currentUser = {
        ...data.user,
        session_type: data.user?.session_type || getActiveSessionType(),
      };
      publishUser(currentUser);
      return currentUser;
    } finally {
      if (ownsTransition(owner)) setLoading(false);
    }
  }, [ownsTransition, requireTransition, publishUser]);

  const loginOffline = useCallback(async (username, password) => {
    const owner = beginSessionTransition();
    try {
      if (window.lvbDesktop) {
        await clearStoredSession(owner);
        requireTransition(owner);
        setSessionBinding(null);
      }
      const data = await offlineAdminLogin(username, password);
      requireTransition(owner);
      const stored = await storeOfflineSession(data.token, data.user, owner);
      requireTransition(owner);
      if (!stored) throw new Error('Sign-in was superseded by a newer authentication action.');
      const currentUser = {
        ...data.user,
        session_type: 'offline',
        offline_reauthenticated: true,
      };
      publishUser(currentUser);
      return currentUser;
    } finally {
      if (ownsTransition(owner)) setLoading(false);
    }
  }, [ownsTransition, requireTransition, publishUser]);

  const logout = useCallback(() => {
    clearStoredSession();
    setSessionBinding(null);
    setLoading(false);
  }, []);

  const getToken = useCallback(() => getActiveAuthToken(), []);

  useEffect(() => {
    const handleInvalidSession = (event) => {
      if (!ownsTransition(event.detail?.owner)) return;
      setSessionBinding(null);
      setLoading(false);
    };
    window.addEventListener(AUTH_INVALID_EVENT, handleInvalidSession);
    return () => window.removeEventListener(AUTH_INVALID_EVENT, handleInvalidSession);
  }, [ownsTransition]);

  return (
    <AuthContext.Provider value={{
      user,
      sessionBinding,
      loading,
      login,
      loginOffline,
      logout,
      getToken,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
